<template>
  <b-sidebar
    id="scanner-sidebar"
    shadow
    backdrop
    right
    :visible="isOpen"
    class="scanner-wide-sidebar"
    header-class="d-none"
    body-class="bg-white"
    @change="handleSidebarChange"
  >
    <template #default>
      <div class="scanner-content p-4">
        <div class="modern-scanner-card mt-1">
          <!-- Header -->
          <div class="card-header-clean d-flex justify-content-between align-items-center mb-1">
            <div class="d-flex align-items-center font-weight-bold text-dark" style="font-size: 1rem;">
               <b-icon-upc-scan class="mr-2 font-weight-bold" />
               Scan colis
            </div>
            <div class="d-flex align-items-center text-muted font-weight-bold">
               <span class="mr-3" style="font-size: 0.75rem;">Poste 04</span>
               <b-icon-x class="cursor-pointer" font-scale="1.2" @click="closeDrawer" />
            </div>
          </div>

          <div class="card-body-clean">
             <!-- Input Section -->
             <div class="mb-4">
                <label class="text-dark font-weight-bold mb-2 ml-1" style="font-size: 0.75rem;">Identifiant du colis</label>
                <div class="d-flex w-100 align-items-center">
                   <div class="custom-search-input flex-grow-1 d-flex align-items-center mr-3 px-3 py-1">
                     <b-icon-upc-scan class="text-muted mr-2" font-scale="1" />
                     <input
                       ref="searchInput"
                       v-model="inputOrderId"
                       type="text"
                       class="form-control border-0 bg-transparent shadow-none p-0 flex-grow-1 font-weight-bold"
                       style="font-family: monospace; font-size: 0.85rem; color: #4B5563;"
                       placeholder="Scanner ou saisir l'ID..."
                       @keyup.enter="addOrderToScanner"
                     />
                   </div>
                   <b-button
                     class="btn-dark-custom px-4 py-1 font-weight-bold"
                     style="font-size: 0.85rem;"
                     :disabled="!inputOrderId.trim() || addingOrder"
                     @click="addOrderToScanner"
                   >
                     Ajouter
                   </b-button>
                </div>
                <div v-if="inputError" class="text-danger small mt-2 ml-1 font-weight-bold">{{ inputError }}</div>
             </div>

             <!-- Summary Section -->
             <div class="d-flex align-items-baseline mb-4 mt-2 ml-1">
                <span class="mr-2 text-dark font-weight-bold" style="font-size: 2rem; font-family: serif; letter-spacing: -1px;">{{ scannedOrders.length }}</span>
                <span class="text-dark font-weight-bold" style="font-size: 0.85rem;">colis scannés</span>
             </div>

             <!-- Divider -->
             <hr class="card-divider" />

             <!-- Scanned Orders Section -->
             <div class="mt-4">
                <div class="d-flex justify-content-between align-items-center mb-3">
                   <h6 class="mb-0 text-dark font-weight-bold ml-1" style="font-size: 0.85rem;">Liste des colis</h6>
                   <div class="d-flex align-items-center gap-2">
                      <span class="text-muted font-weight-bold" style="font-size: 0.75rem;">{{ scannedOrders.length }} colis</span>
                      <b-button
                        v-if="scannedOrders.length > 0"
                        variant="outline-danger"
                        size="sm"
                        class="btn-clear-all px-2"
                        style="font-size: 0.75rem; padding: 0.25rem 0.5rem !important; height: auto;"
                        @click="clearAllScannedOrders"
                        title="Effacer tous les colis scannés"
                      >
                         <b-icon-trash font-scale="0.8" class="mr-1" />
                         Effacer tout
                      </b-button>
                   </div>
                </div>

                <div v-if="scannedOrders.length === 0" class="text-center py-5 text-muted font-weight-bold" style="font-size: 0.85rem;">
                    Scanner un colis pour l'ajouter à la liste
                </div>

                <div v-else class="scanned-list d-flex flex-column" style="gap: 0.5rem;">
                   <div
                     v-for="scannedItem in scannedOrders"
                     :key="scannedItem.id"
                     class="custom-order-row d-flex align-items-center py-2 pl-3 pr-2 position-relative"
                   >
                      <!-- Accent border left -->
                      <div class="row-accent" :class="getAccentClass(scannedItem)"></div>

                      <!-- Icon -->
                      <div class="mr-2" :class="getIconColorClass(scannedItem)" style="display:flex; align-items:center;">
                         <b-icon-exclamation-triangle v-if="isAnomaly(scannedItem)" font-scale="1" />
                         <b-icon-check-circle v-else-if="isSuccess(scannedItem)" font-scale="1" />
                         <b-icon-box v-else font-scale="1" />
                      </div>

                      <!-- ID & Barcode -->
                      <div class="flex-grow-1 d-flex align-items-center min-w-0 mr-2">
                         <span class="font-weight-bold text-dark mr-2" style="font-size: 0.85rem;">#{{ scannedItem.id }}</span>
                         <span class="text-muted text-truncate font-weight-bold" style="font-size: 0.70rem; letter-spacing: 0.2px;">{{ scannedItem.barcode || '' }}</span>
                         <b-badge v-if="scannedItem.isDuplicate" class="ml-2" variant="warning" style="font-size: 0.65rem;">Doublon</b-badge>
                      </div>

                      <!-- Status Badge -->
                      <div v-if="scannedItem.order" class="mx-2 modern-badge-pill" :class="getCustomBadgeClass(scannedItem.order.status_label)">
                         {{ scannedItem.order.status_label }}
                      </div>
                      <div v-else-if="scannedItem.loading" class="mx-2 text-primary small my-1">
                         <b-spinner small />
                      </div>
                      <div v-else-if="scannedItem.error" class="mx-2 text-danger font-weight-bold my-1" style="font-size: 0.70rem;">
                         Erreur
                      </div>

                      <!-- Date -->
                      <div v-if="scannedItem.order" class="text-muted mx-2 font-weight-bold text-nowrap" style="font-size: 0.70rem;">
                         {{ formatDateTime(scannedItem.order.last_activity_at) }}
                      </div>

                      <!-- Delete -->
                      <div class="ml-1 pl-1">
                        <b-icon-x class="cursor-pointer text-muted close-icon-hover" font-scale="1.2" @click="removeOrderFromScanner(scannedItem.id)" />
                      </div>
                   </div>
                </div>
             </div>
          </div>
        </div>

      </div>
    </template>
  </b-sidebar>
</template>

<script>
import {
  BBadge, BButton, BIconX, BSidebar, BSpinner, BIconUpcScan, BIconBox, BIconExclamationTriangle, BIconCheckCircle, BIconTrash,
} from 'bootstrap-vue'
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import alreadyScanned from '@/assets/audio/colis-deja-scanne.mp3'

const STORAGE_KEY = 'stale_orders_scanned'

export default {
  name: 'OrderStatusScannerDrawer',
  components: {
    BBadge,
    BButton,
    BIconX,
    BSidebar,
    BSpinner,
    BIconUpcScan,
    BIconBox,
    BIconExclamationTriangle,
    BIconCheckCircle,
    BIconTrash,
  },
  props: {
    open: {
      type: Boolean,
      default: false,
    },
    staleHours: {
      type: Number,
      default: 8,
    },
  },
  data() {
    return {
      inputOrderId: '',
      inputError: '',
      scannedOrders: [],
      addingOrder: false,
      checkingStatus: false,
      toastVisible: false,
      toastVariant: 'info',
      toastMessage: '',
      alreadyScanned,
    }
  },
  computed: {
    isOpen() {
      return this.open
    },
  },
  watch: {
    open(newVal) {
      if (newVal) {
        this.loadScannedOrders()
      }
    },
  },
  mounted() {
    this.loadScannedOrders()
  },
  methods: {
    // Session Storage Methods
    loadScannedOrders() {
      try {
        const stored = sessionStorage.getItem(STORAGE_KEY)
        if (stored) {
          this.scannedOrders = JSON.parse(stored)
        }
      } catch (error) {
        // Silently handle storage errors
      }
    },
    saveScannedOrders() {
      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(this.scannedOrders))
      } catch (error) {
        // Silently handle storage errors
      }
    },
    clearStoredScannedOrders() {
      try {
        sessionStorage.removeItem(STORAGE_KEY)
      } catch (error) {
        // Silently handle storage errors
      }
    },
    resetScannerState() {
      this.inputOrderId = ''
      this.inputError = ''
      this.scannedOrders = []
      this.clearStoredScannedOrders()
    },
    handleSidebarChange(isVisible) {
      if (!isVisible) {
        this.resetScannerState()
      }
      this.$emit('update:open', isVisible)
    },
    closeDrawer() {
      this.resetScannerState()
      this.$emit('update:open', false)
    },
    // Order Management
    async addOrderToScanner() {
      const orderId = this.inputOrderId.trim()
      this.inputError = ''

      if (!orderId) {
        this.inputError = 'Veuillez entrer un ID de colis'
        return
      }

      // Check for duplicate
      const isDuplicate = this.scannedOrders.some(item => item.id === orderId)
      if (isDuplicate) {
        this.playAudio(this.alreadyScanned)
        this.showToast('warning', 'Ce colis a déjà été scanné')
        this.inputOrderId = ''
        this.focusInput()
        return
      }

      // Add to scanned list
      const newScannedItem = {
        id: orderId,
        barcode: null,
        order: null,
        statusHistory: [],
        loading: true,
        error: null,
        isDuplicate: false,
      }

      this.scannedOrders.push(newScannedItem)
      this.saveScannedOrders()
      this.inputOrderId = ''
      this.showToast('success', 'Colis ajouté, chargement des informations...')

      // Automatically fetch order status
      await this.fetchOrderStatus(newScannedItem)
      this.saveScannedOrders()
      this.focusInput()
    },
    removeOrderFromScanner(orderId) {
      this.scannedOrders = this.scannedOrders.filter(item => item.id !== orderId)
      this.saveScannedOrders()
      this.showToast('info', 'Colis supprimé de la liste')
    },
    clearAllScannedOrders() {
      if (this.scannedOrders.length === 0) return

      this.scannedOrders = []
      this.saveScannedOrders()
      this.showToast('info', 'Tous les colis ont été effacés')
    },
    // Status Checking
    async checkAllOrdersStatus() {
      if (this.scannedOrders.length === 0) return

      this.checkingStatus = true

      // Fetch all orders in parallel
      await Promise.all(
        this.scannedOrders.map(scannedItem => this.fetchOrderStatus(scannedItem)),
      )

      this.saveScannedOrders()
      this.checkingStatus = false

      const successCount = this.scannedOrders.filter(item => item.order).length
      const errorCount = this.scannedOrders.filter(item => item.error).length

      if (errorCount === 0) {
        this.showToast('success', `${successCount} colis vérifié(s)`)
      } else {
        this.showToast('warning', `${successCount} vérifié(s), ${errorCount} erreur(s)`)
      }
    },
    async fetchOrderStatus(scannedItem) {
      // Create a reference to mutate the original object
      const item = scannedItem
      item.loading = true
      item.error = null
      item.order = null
      item.statusHistory = []

      try {
        const { data } = await axios.get('/api/orders/scan-status/', {
          params: {
            order_id: item.id,
            hours: this.staleHours,
          },
        })

        item.order = data.order || null
        item.statusHistory = data.status_history || []
        item.barcode = data.order?.barcode || null

        // Check if this is a duplicate scan (added multiple times)
        const occurrences = this.scannedOrders.filter(itm => itm.id === item.id).length
        item.isDuplicate = occurrences > 1
        // Trigger Vue reactivity
        this.$set(item, 'isDuplicate', item.isDuplicate)
      } catch (error) {
        item.error = error.response?.data?.error || 'Erreur lors de la récupération du statut'
      } finally {
        item.loading = false
      }
    },
    // Utility Methods
    isAnomaly(item) {
      if (item.error) return true;
      const status = item.order?.status_label?.toLowerCase() || '';
      return status.includes('retour') || status.includes('anomalie') || status.includes('annulé') || status.includes('refusé');
    },
    isSuccess(item) {
      const status = item.order?.status_label?.toLowerCase() || '';
      return status.includes('livré') || status.includes('payé') || status.includes('clôturé');
    },
    getAccentClass(item) {
      if (this.isAnomaly(item)) return 'bg-accent-danger';
      if (this.isSuccess(item)) return 'bg-accent-success';
      if (item.order) return 'bg-accent-primary';
      return 'bg-accent-secondary';
    },
    getIconColorClass(item) {
      if (this.isAnomaly(item)) return 'text-danger-icon';
      if (this.isSuccess(item)) return 'text-success-icon';
      if (item.order) return 'text-primary-icon';
      return 'text-muted';
    },
    getCustomBadgeClass(status) {
      if (!status) return 'modern-badge-default';
      const s = status.toLowerCase();
      if (s.includes('livré') || s.includes('payé') || s.includes('clôturé')) return 'modern-badge-success';
      if (s.includes('retour') || s.includes('anomalie') || s.includes('annulé') || s.includes('refusé')) return 'modern-badge-danger';
      if (s.includes('dépôt') || s.includes('réception') || s.includes('tri') || s.includes('transit')) return 'modern-badge-info';
      if (s.includes('cours') || s.includes('livraison') || s.includes('échange')) return 'modern-badge-primary';
      return 'modern-badge-warning';
    },
    formatDateTime(value) {
      if (!value) return '-'
      return new Date(value).toLocaleString('fr-FR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
    focusInput() {
      this.$nextTick(() => {
        if (this.$refs.searchInput) {
          this.$refs.searchInput.focus()
        }
      })
    },
    playAudio(audio) {
      if (!audio) return

      try {
        const voice = new Audio(audio)
        voice.play().catch(() => {})
      } catch (error) {
        // Audio feedback is optional; keep scanning flow uninterrupted.
      }
    },
    showToast(variant, message) {
      this.toastVariant = variant
      this.toastMessage = message
      this.toastVisible = true

      if (this.$toast) {
        this.$toast(
          {
            component: ToastificationContent,
            props: {
              title: 'Notification',
              icon: variant === 'warning' ? 'AlertTriangleIcon' : 'InfoIcon',
              text: message,
              variant,
            },
          },
          {
            position: 'top-center',
          },
        )
        return
      }

      if (this.$bvToast) {
        this.$bvToast.toast(message, {
          title: 'Notification',
          variant,
          solid: true,
          toaster: 'b-toaster-top-right',
          autoHideDelay: 3500,
        })
      }
    },
  },
}
</script>

<style lang="scss" scoped>

/* Modern Variables */
$primary-color: #4073AA;
$primary-hover: #5789BF;
$danger-color: #E83C39;
$bg-light-color: #F8FAFC;
$text-dark: #2c3e50;
$border-radius: 12px;
$card-shadow: 0 4px 16px rgba(0,0,0,0.04);
$transition-speed: 0.3s;

/* Typography & Layout */
.letter-spacing-1 {
  letter-spacing: 0.5px;
}

.font-weight-medium {
  font-weight: 500;
}

.bg-light-modern {
  background-color: $bg-light-color !important;
}

.bg-light-soft {
  background-color: #F8FAFC;
}

.scanner-header {
  padding: 1.5rem 2rem;
  background-color: #ffffff;
  border-bottom: 1px solid #edf2f9;
}

.scanner-content {
  padding: 2rem;
  height: 100%;
  overflow-y: auto;
}

/* Modern Components */
.modern-scanner-card {
  background-color: #ffffff;
  border-radius: 8px;
  border: none;
  box-shadow: none;
}

.card-header-clean {
  padding: 0.5rem 0.5rem 1.25rem 0.5rem;
  border-bottom: 1px solid #e5e7eb;
}

.card-body-clean {
  padding: 1.5rem 0.5rem;
}

.custom-search-input {
  background-color: #F8F7F3;
  border-radius: 8px;
  min-height: 36px;
  max-height: 36px;

  input:focus {
    outline: none;
  }
}

.btn-dark-custom {
  background-color: #6B7280 !important;
  color: #ffffff !important;
  border: none;
  border-radius: 8px;
  min-height: 36px;
  max-height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.2s;

  &:hover, &:focus {
    outline: none;
    opacity: 0.9;
    box-shadow: none;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.card-divider {
  border-top: 1px solid #e5e7eb;
  margin: 1.5rem 0.5rem;
  border-bottom: 0;
}

.custom-order-row {
  background-color: #F8F7F3;
  border-radius: 8px;
  min-height: 48px;
}

.row-accent {
  position: absolute;
  left: 0;
  top: 15%;
  bottom: 15%;
  width: 4px;
  border-radius: 0 4px 4px 0;
}

.bg-accent-danger { background-color: #9b1c1c; }
.bg-accent-success { background-color: #03543f; }
.bg-accent-primary { background-color: #1e429f; }
.bg-accent-secondary { background-color: #6b7280; }

.text-danger-icon { color: #9b1c1c; }
.text-success-icon { color: #03543f; }
.text-primary-icon { color: #1e429f; }

.modern-badge-pill {
  padding: 0.35em 0.8em;
  border-radius: 50rem;
  font-weight: 700;
  font-size: 0.75rem;
  white-space: nowrap;
}

.modern-badge-default { background-color: #f1f5f9; color: #475569; }
.modern-badge-success { background-color: #def7ec; color: #03543f; }
.modern-badge-danger { background-color: #fde8e8; color: #9b1c1c; }
.modern-badge-info { background-color: #e1effe; color: #1e429f; }
.modern-badge-primary { background-color: #e1effe; color: #1e429f; }
.modern-badge-warning { background-color: #fdf6b2; color: #723b13; }

.close-icon-hover {
  transition: color 0.2s ease;
  &:hover {
    color: #4b5563 !important;
  }
}

.mobile-only-status {
  display: flex !important;
}

@media (min-width: 768px) {
  .mobile-only-status {
    display: none !important;
  }
}
@media (max-width: 767.98px) {
  .hide-on-mobile {
    display: none !important;
  }
}

.btn-history-toggle {
  color: $text-dark;
  background-color: $bg-light-color;
  transition: all 0.2s ease;

  &:hover {
    background-color: #e2e8f0;
    text-decoration: none;
  }
}

.btn-clear-all {
  border-color: #ef4444 !important;
  color: #ef4444 !important;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  white-space: nowrap;

  &:hover {
    background-color: #fef2f2 !important;
    border-color: #dc2626 !important;
    color: #dc2626 !important;
  }

  &:focus {
    box-shadow: none;
  }
}

.gap-2 {
  gap: 0.5rem;
}

/* History Timeline */
.modern-history-timeline {
  position: relative;
  margin-top: 1rem;
  padding-left: 1rem;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 15px;
    width: 2px;
    background-color: #edf2f9;
  }

  .history-item {
    position: relative;
    padding-left: 1.5rem;

    .history-marker {
      position: absolute;
      left: -5px;
      top: 5px;
      width: 12px;
      height: 12px;
      border-radius: 50%;
      background-color: #fff;
      border: 2px solid $primary-hover;
      z-index: 1;

      &.status-success { border-color: #10b981; }
      &.status-danger { border-color: #ef4444; }
      &.status-info { border-color: #0ea5e9; }
      &.status-primary { border-color: #4073aa; }
      &.status-warning { border-color: #f59e0b; }
    }
  }
}

.compact-timeline {
  margin-top: 0 !important;
  &::before { left: 10px; }
  .history-item { padding-left: 1rem; }
  .history-marker { left: -6px; top: 3px; width: 10px; height: 10px; }
}

.border-left-primary {
  border-left: 3px solid $primary-color;
}

.modern-alert-danger {
  background-color: rgba(232, 60, 57, 0.05);
  color: $danger-color;
  border-radius: 8px;
}

.alert-sm {
  padding: 0.75rem 1rem;
  font-size: 0.875rem;
}

::v-deep .b-sidebar {
  z-index: 1050;
}

/* Wide sidebar styling */
.scanner-wide-sidebar {
  width: 750px !important;
}

#scanner-sidebar {
  width: 750px !important;
}

::v-deep .scanner-wide-sidebar {
  width: 750px !important;
}

::v-deep #scanner-sidebar {
  width: 750px !important;
}

::v-deep .scanner-wide-sidebar.b-sidebar {
  width: 750px !important;
}

::v-deep .b-sidebar.show {
  width: 750px !important;
}

::v-deep .b-sidebar-aside {
  width: 750px !important;
  max-width: 100vw !important;
  min-width: 320px !important;
}

::v-deep .b-sidebar-backdrop {
  width: 100% !important;
  right: 0;
}

@media (max-width: 1200px) {
  .scanner-wide-sidebar,
  #scanner-sidebar,
  ::v-deep .scanner-wide-sidebar,
  ::v-deep #scanner-sidebar,
  ::v-deep .scanner-wide-sidebar.b-sidebar,
  ::v-deep .b-sidebar.show,
  ::v-deep .b-sidebar-aside {
    width: 600px !important;
  }
}

@media (max-width: 576px) {
  .scanner-wide-sidebar,
  #scanner-sidebar,
  ::v-deep .scanner-wide-sidebar,
  ::v-deep #scanner-sidebar,
  ::v-deep .scanner-wide-sidebar.b-sidebar,
  ::v-deep .b-sidebar.show,
  ::v-deep .b-sidebar-aside {
    width: 100vw !important;
    max-width: 100vw !important;
  }
}
</style>
